import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction } from "react"
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline"
import { Table } from "@/components/general/Table"
import { DailyMileageType } from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"
import Swal from "sweetalert2"

type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { DailyMileage: DailyMileageType[] }
  header: {
    name: string
    width: string
  }[]
  handleFormUpdate: (form: DailyMileageType) => void
}

export const DailyMileage: FC<Props> = (props) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<DailyMileageType>()

  const onSubmit = (data: DailyMileageType) => {
    props.handleFormUpdate(data)
  }

  const populateData = (e: DailyMileageType) => {
    setValue("id", e.id)
    setValue("perDayMileage", e.perDayMileage)
    setValue("sameDayMileage", e.sameDayMileage)
    props.setPopup(true)
  }

  useResetOnClose(props.popup, () => {
    reset({
      id: "",
      perDayMileage: undefined,
      sameDayMileage: undefined,
    })
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="small"
        title="Update Mileage"
      >
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2">
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Per Day Mileage</label>
              <input
                className={
                  (errors.perDayMileage
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2 border-2 rounded placeholder:text-xs"
                }
                type="number"
                {...register("perDayMileage", { required: true })}
              />
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Same Day Mileage</label>
              <input
                className={
                  (errors.sameDayMileage
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2 border-2 rounded placeholder:text-xs"
                }
                type="number"
                {...register("sameDayMileage", { required: true })}
              />
            </fieldset>
          </div>
          <button
            onClick={handleSubmit(onSubmit)}
            className=" bg-primary-8 px-4 py-2 rounded font-medium w-full dark:text-secondary-2 text-primary-1"
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.DailyMileage.map((e, i) => (
            <tr key={i} className="text-left">
              <td className={"whitespace-nowrap p-4 w-72"}>
                {e.perDayMileage}
              </td>
              <td className={"whitespace-nowrap p-4 w-72"}>
                {e.sameDayMileage}
              </td>
              <td className={"whitespace-nowrap p-4 w-24"}>
                <div className="flex justify-end gap-2">
                  <PencilSquareIcon
                    onClick={() => populateData(e)}
                    className="w-4 h-4 text-accent cursor-pointer"
                  />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
